// Desktop News Feed Component - Modern Professional Design
import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { WordPressPost } from '../types/wordpress';
import { fetchPosts, checkForNewContent, mergeNewPosts } from '../services/api';
import { useResponsiveImage } from '../hooks/useOptimizedImage';
import { useLanguageContext } from '../contexts/LanguageContext';
import { formatDate } from '../utils/dateUtils';
import TikTokFeed from './TikTokFeed';
import EmbeddedAdSense from './EmbeddedAdSense';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
`;

const Header = styled.header`
  padding: 40px 0;
  text-align: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  margin: 0 -24px 40px -24px;
  color: white;
  position: relative;
`;

const HeaderTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
  letter-spacing: -1px;
`;

const HeaderSubtitle = styled.p`
  font-size: 1.2rem;
  margin: 16px 0 0 0;
  opacity: 0.9;
  font-weight: 300;
`;

// NEW: Auto-refresh indicator
const RefreshIndicator = styled.div<{ visible: boolean; newContent: boolean }>`
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${props => props.newContent ? 'rgba(46, 204, 113, 0.9)' : 'rgba(255, 255, 255, 0.1)'};
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  opacity: ${props => props.visible ? 1 : 0};
  transform: ${props => props.visible ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const RefreshIcon = styled.div<{ spinning: boolean }>`
  width: 16px;
  height: 16px;
  border: 2px solid currentColor;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: ${props => props.spinning ? 'spin 1s linear infinite' : 'none'};
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// NEW: New content notification
const NewContentBanner = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  padding: 12px;
  text-align: center;
  font-weight: 600;
  z-index: 1000;
  transform: ${props => props.visible ? 'translateY(0)' : 'translateY(-100%)'};
  transition: transform 0.3s ease;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
`;

const FeedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  padding-bottom: 60px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ArticleCard = styled.div<{ isNew?: boolean }>`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  
  ${props => props.isNew && `
    border: 2px solid #4CAF50;
    animation: newContentPulse 2s ease-in-out;
  `}
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #667eea, #764ba2);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
  
  @keyframes newContentPulse {
    0%, 100% { box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1); }
    50% { box-shadow: 0 8px 32px rgba(76, 175, 80, 0.3), 0 0 20px rgba(76, 175, 80, 0.2); }
  }
`;

// NEW: New post badge
const NewPostBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
`;

// Modern Loading Animations
const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  height: 240px;
  overflow: hidden;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
`;

const ImagePlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a0aec0;
  font-size: 2rem;
`;

const ArticleImage = styled.img<{ $isLoaded?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.3s ease;
  opacity: ${props => props.$isLoaded ? 1 : 0};
  transform: ${props => props.$isLoaded ? 'scale(1)' : 'scale(1.1)'};
  
  ${ArticleCard}:hover & {
    transform: ${props => props.$isLoaded ? 'scale(1.05)' : 'scale(1.1)'};
  }
`;

const ArticleContent = styled.div`
  padding: 24px;
`;

const ArticleTitle = styled.h2`
  margin: 0 0 12px 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: #2d3748;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ArticleExcerpt = styled.p`
  color: #718096;
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0 0 20px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ArticleFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
`;

const PublishDate = styled.span`
  font-size: 0.85rem;
  color: #a0aec0;
  font-weight: 500;
`;

const ReadMoreButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 25px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  }
`;

// Enhanced Loading Container
const LoadingContainer = styled.div`
  padding: 0 0 60px 0;
`;

const LoadingHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const LoadingTitle = styled.div`
  height: 48px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 8px;
  margin: 0 auto 16px;
  width: 300px;
`;

const LoadingSubtitle = styled.div`
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  animation-delay: 0.2s;
  border-radius: 6px;
  margin: 0 auto;
  width: 400px;
`;

// Skeleton Grid
const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// Skeleton Card
const SkeletonCard = styled.div<{ delay?: number }>`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  animation: ${fadeInUp} 0.6s ease-out;
  animation-delay: ${props => props.delay || 0}s;
  animation-fill-mode: both;
`;

const SkeletonImage = styled.div`
  height: 240px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
`;

const SkeletonContent = styled.div`
  padding: 24px;
`;

const SkeletonTitle = styled.div`
  height: 24px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 6px;
  margin-bottom: 12px;
`;

const SkeletonTitleSecond = styled.div`
  height: 24px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  animation-delay: 0.1s;
  border-radius: 6px;
  margin-bottom: 20px;
  width: 75%;
`;

const SkeletonText = styled.div<{ width?: string; delay?: number }>`
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  animation-delay: ${props => props.delay || 0}s;
  border-radius: 4px;
  margin-bottom: 8px;
  width: ${props => props.width || '100%'};
`;

const SkeletonFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
`;

const SkeletonDate = styled.div`
  height: 14px;
  width: 120px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  animation-delay: 0.3s;
  border-radius: 4px;
`;

const SkeletonButton = styled.div`
  height: 40px;
  width: 100px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  animation-delay: 0.4s;
  border-radius: 20px;
`;

// Loading Status Indicator
const LoadingStatus = styled.div`
  text-align: center;
  margin-top: 40px;
  animation: ${fadeInUp} 0.6s ease-out;
  animation-delay: 0.8s;
  animation-fill-mode: both;
`;

const LoadingDots = styled.div`
  display: inline-flex;
  gap: 4px;
  margin-left: 8px;
`;

const LoadingDot = styled.div<{ delay: number }>`
  width: 8px;
  height: 8px;
  background: #667eea;
  border-radius: 50%;
  animation: ${pulse} 1.4s infinite;
  animation-delay: ${props => props.delay}s;
`;

const LoadingText = styled.span`
  color: #667eea;
  font-weight: 600;
  font-size: 1.1rem;
`;

// Skeleton Loading Component
const SkeletonLoader: React.FC = () => {
  return (
    <LoadingContainer>
      <LoadingHeader>
        <LoadingTitle />
        <LoadingSubtitle />
      </LoadingHeader>
      
      <SkeletonGrid>
        {[...Array(6)].map((_, index) => (
          <SkeletonCard key={index} delay={index * 0.1}>
            <SkeletonImage />
            <SkeletonContent>
              <SkeletonTitle />
              <SkeletonTitleSecond />
              <SkeletonText width="100%" delay={0.1} />
              <SkeletonText width="85%" delay={0.2} />
              <SkeletonText width="60%" delay={0.3} />
              <SkeletonFooter>
                <SkeletonDate />
                <SkeletonButton />
              </SkeletonFooter>
            </SkeletonContent>
          </SkeletonCard>
        ))}
      </SkeletonGrid>
      
      <LoadingStatus>
        <LoadingText>
          Awesome content is loading
          <LoadingDots>
            <LoadingDot delay={0} />
            <LoadingDot delay={0.2} />
            <LoadingDot delay={0.4} />
          </LoadingDots>
        </LoadingText>
      </LoadingStatus>
    </LoadingContainer>
  );
};

// Empty State Components
const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #718096;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 16px;
  opacity: 0.5;
`;

// Loading More Spinner
const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingMoreContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  font-size: 1.1rem;
  color: #718096;
`;

// Lazy Image Component
interface LazyImageProps {
  src: string;
  alt: string;
  placeholder?: string;
  isMobile?: boolean;
  context?: 'news-feed-grid' | 'post-card' | 'featured-card';
}

const LazyImage: React.FC<LazyImageProps> = ({ 
  src, 
  alt, 
  placeholder = '🖼️', 
  isMobile = false,
  context = 'news-feed-grid'
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  
  // Use optimized image based on context
  const optimizedImage = useResponsiveImage(src, context, isMobile);

  const handleLoad = () => {
    setIsLoaded(true);
    setHasError(false);
  };

  const handleError = () => {
    console.warn('Optimized image failed, fallback to original:', optimizedImage.src);
    setHasError(true);
    setIsLoaded(false);
  };

  const imageSrc = hasError ? optimizedImage.originalSrc : optimizedImage.src;

  return (
    <ImageContainer>
      {!isLoaded && !hasError && (
        <ImagePlaceholder>
          {placeholder}
        </ImagePlaceholder>
      )}
      {hasError && (
        <ImagePlaceholder>
          📷
        </ImagePlaceholder>
      )}
      <ArticleImage
        src={imageSrc}
        alt={alt}
        $isLoaded={isLoaded}
        onLoad={handleLoad}
        onError={handleError}
        loading="lazy"
        title={`${alt} - Optimized: ${optimizedImage.isOptimized ? optimizedImage.size : 'Original'}`}
      />
    </ImageContainer>
  );
};

const NewsFeed: React.FC = () => {
  const [posts, setPosts] = useState<WordPressPost[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const perPage = 20;
  
  // Language detection integration
  const { getCurrentLanguageCategoryId, getCurrentLanguageCode, isInitialized: isLanguageInitialized } = useLanguageContext();
  
  // NEW: Auto-refresh states
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [hasNewContent, setHasNewContent] = useState<boolean>(false);
  const [showNewContentBanner, setShowNewContentBanner] = useState<boolean>(false);
  const [newPostIds, setNewPostIds] = useState<Set<number>>(new Set());
  const [lastUpdateTime, setLastUpdateTime] = useState<string>('');
  
  // Refs for cleanup
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const newContentTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState<boolean>(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 🔧 MOBILE LANGUAGE FIX: Load posts with enhanced mobile language detection
  const loadPosts = useCallback(async (pageNum: number = 1, append: boolean = false) => {
    try {
      if (!append) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      // 🚨 CRITICAL MOBILE LOCALHOST FIX: Enhanced waiting for language detection
      let waitCount = 0;
      const maxWait = 20; // Max 2 seconds wait (20 * 100ms)
      
      while (!isLanguageInitialized && waitCount < maxWait) {
        console.log(`🌍 [NewsFeed] Waiting for language detection... (${waitCount + 1}/${maxWait})`);
        await new Promise(resolve => setTimeout(resolve, 100)); // 100ms intervals
        waitCount++;
      }

      const languageCategoryId = getCurrentLanguageCategoryId();
      
      // 🔧 MOBILE DEBUG: Enhanced device and language info logging
      const isMobileDevice = window.innerWidth <= 768;
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      console.log('📱 [NewsFeed] Load posts - Complete Debug Info:', {
        deviceType: isMobileDevice ? 'mobile' : 'desktop',
        environment: isLocalhost ? 'localhost' : 'production', 
        pageNum,
        append,
        languageCategoryId,
        isLanguageInitialized,
        waitedMs: waitCount * 100,
        browserLanguages: navigator.languages || [navigator.language],
        userAgent: navigator.userAgent.includes('Mobile') ? 'Mobile' : 'Desktop',
        windowWidth: window.innerWidth,
        hostname: window.location.hostname
      });

      // ✅ ENSURE LANGUAGE FILTERING: Always include language category for filtering
      const data = await fetchPosts(
        pageNum, 
        perPage, 
        undefined, // categories
        undefined, // tags
        false, // forceNoCache
        languageCategoryId || undefined // 🎯 CRITICAL: Language filtering parameter (handle null)
      );

      console.log(`✅ [NewsFeed] ${isMobileDevice ? '📱' : '💻'}${isLocalhost ? '🏠' : '🌐'} Loaded ${data.length} posts (page ${pageNum}) with language filter: ${languageCategoryId || 'none'}`);

      if (append) {
        setPosts(prev => [...prev, ...data]);
      } else {
        setPosts(data);
      }

      setHasMore(data.length === perPage);
      setPage(pageNum);
    } catch (err) {
      console.error('❌ [NewsFeed] Error loading posts:', err);
      // Don't show error on mobile to prevent user disruption
      if (!isMobile) {
        setError('Failed to load posts');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [isLanguageInitialized, getCurrentLanguageCategoryId, perPage, isMobile]);

  // ✅ ANTI-BLOCKING: Emergency fallback if language detection completely fails
  useEffect(() => {
    // 🚨 CRITICAL USER FEEDBACK: Emergency fallback removed completely
    // User reported: Emergency fallback triggers before trace completes, causing API calls without language filter
    // NEW STRATEGY: Always wait for language detection, never bypass it
    
    console.log('[NewsFeed] 🚨 EMERGENCY FALLBACK DISABLED - All API calls must wait for language detection');
    
    // 🔄 NO MORE EMERGENCY BYPASS: If language detection fails, we use browser fallback from useLanguageDetection
    // This ensures ALL API calls have language filtering
    
    // OLD CODE (REMOVED):
    // - Emergency timeout that loads posts without language filtering
    // - Bypass mechanism that creates unfiltered URLs
    
    // NEW BEHAVIOR:
    // - Always wait for isLanguageInitialized = true
    // - useLanguageDetection has its own browser fallback (en/vi/zh) 
    // - No API calls without language parameters
    
    return () => {
      // No timeout to clear since emergency fallback is removed
      console.log('[NewsFeed] ✅ No emergency timeout - clean exit');
    };
  }, []);

  // NEW: Auto-refresh setup
  useEffect(() => {
    // 🔇 DISABLED: Auto-refresh disabled to prevent annoying update notifications
    const setupAutoRefresh = () => {
      console.log('[NewsFeed] 🔇 Auto-refresh disabled by user request');
      
      // No more auto-checking for new content
      // No more periodic updates
      // No more annoying notifications about new posts
      
      return () => {
        // Cleanup (but nothing to clean since auto-refresh is disabled)
        console.log('[NewsFeed] 🔇 Auto-refresh cleanup (disabled)');
      };
    };
    
    // 🔇 DISABLED: Auto-refresh feature completely disabled
    // if (posts.length > 0 && lastUpdateTime) {
    //   const cleanup = setupAutoRefresh();
    //   return cleanup;
    // }
  }, [posts, lastUpdateTime, isRefreshing, loading]);

  // Handle new content banner click
  const handleNewContentClick = () => {
    setShowNewContentBanner(false);
    setHasNewContent(false);
    // Scroll to top to show new content
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Load initial posts
  useEffect(() => {
    if (isLanguageInitialized) {
      loadPosts(1, false);
    }
  }, [isLanguageInitialized, loadPosts]);

  // Load more posts
  const loadMorePosts = useCallback(async () => {
    if (hasMore && !loadingMore) {
      await loadPosts(page + 1, true);
    }
  }, [page, hasMore, loadingMore, loadPosts]);

  // Infinite scroll handler - PC optimized for smooth experience
  useEffect(() => {
    const handleScroll = () => {
      if (loading || loadingMore || !hasMore) return;
      
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // 🚀 PC OPTIMIZATION: Load more when user is 800px from bottom (much earlier for smooth scrolling)
      const triggerDistance = isMobile ? 200 : 800;
      if (scrollTop + windowHeight >= documentHeight - triggerDistance) {
        console.log('🔄 PC Auto-loading more posts... Distance from bottom:', documentHeight - (scrollTop + windowHeight));
        loadMorePosts();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loading, loadingMore, hasMore, page, posts, isMobile, loadMorePosts]);

  // Clean HTML content
  const cleanHtml = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || '';
  };

  // Handle article click
  const handleArticleClick = (post: WordPressPost) => {
    // Use same URL structure as mobile: /post/:id/:slug.html
    const targetPath = post.link ? post.link.replace(/https?:\/\/[^\/]+/, '') : `/post/${post.id}/${post.slug || `post-${post.id}`}.html`;
    navigate(targetPath);
  };

  if (loading) {
    return (
      <Container>
        <Helmet>
          <title>Infonium - High-Quality News and Information | Desktop News Feed</title>
          <meta name="description" content="Discover high-quality news and information on Infonium. Modern desktop interface with professional news feed layout, continuously updated with trending articles." />
          <meta name="keywords" content="infonium, news, information, desktop, news feed, journalism, articles, high quality, updates" />
          <meta name="author" content="Infonium" />
          <meta property="og:title" content="Infonium - Desktop News Feed" />
          <meta property="og:description" content="Discover high-quality news and information on Infonium with modern desktop interface." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : ''} />
          <meta property="og:image" content="https://cdn.infonium.org/img/logo_infonium.png" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Infonium - Desktop News Feed" />
          <meta name="twitter:description" content="Discover high-quality news and information on Infonium with modern desktop interface." />
          <meta name="twitter:image" content="https://cdn.infonium.org/img/logo_infonium.png" />
        </Helmet>
        <Header>
          <HeaderTitle>Infonium</HeaderTitle>
          <HeaderSubtitle>Your modern information platform</HeaderSubtitle>
        </Header>
        <SkeletonLoader />
      </Container>
    );
  }

  if (posts.length === 0) {
    return (
      <Container>
        <Helmet>
          <title>Infonium - High-Quality News and Information | Desktop News Feed</title>
          <meta name="description" content="Discover high-quality news and information on Infonium. Modern desktop interface with professional news feed layout, continuously updated with trending articles." />
          <meta name="keywords" content="infonium, news, information, desktop, news feed, journalism, articles, high quality, updates" />
          <meta name="author" content="Infonium" />
          <meta property="og:title" content="Infonium - Desktop News Feed" />
          <meta property="og:description" content="Discover high-quality news and information on Infonium with modern desktop interface." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : ''} />
          <meta property="og:image" content="https://cdn.infonium.org/img/logo_infonium.png" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Infonium - Desktop News Feed" />
          <meta name="twitter:description" content="Discover high-quality news and information on Infonium with modern desktop interface." />
          <meta name="twitter:image" content="https://cdn.infonium.org/img/logo_infonium.png" />
        </Helmet>
        <Header>
          <HeaderTitle>Infonium</HeaderTitle>
          <HeaderSubtitle>Your modern information platform</HeaderSubtitle>
        </Header>
        <EmptyState>
          <EmptyIcon>📰</EmptyIcon>
          <h3>No articles found</h3>
          <p>Please try again later or check your network connection</p>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Helmet>
        <title>Infonium - High-Quality News and Information | Desktop News Feed</title>
        <meta name="description" content="Discover high-quality news and information on Infonium. Modern desktop interface with professional news feed layout, continuously updated with trending articles." />
        <meta name="keywords" content="infonium, news, information, desktop, news feed, journalism, articles, high quality, updates" />
        <meta name="author" content="Infonium" />
        <meta property="og:title" content="Infonium - Desktop News Feed" />
        <meta property="og:description" content="Discover high-quality news and information on Infonium with modern desktop interface." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : ''} />
        <meta property="og:image" content="https://cdn.infonium.org/img/logo_infonium.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Infonium - Desktop News Feed" />
        <meta name="twitter:description" content="Discover high-quality news and information on Infonium with modern desktop interface." />
        <meta name="twitter:image" content="https://cdn.infonium.org/img/logo_infonium.png" />
      </Helmet>

      {/* 🔇 DISABLED: Annoying new content banner removed */}
      {/* <NewContentBanner 
        visible={showNewContentBanner}
        onClick={handleNewContentClick}
      >
        🆕 {newPostIds.size} new articles added - Click to view!
      </NewContentBanner> */}

      <Header>
        <HeaderTitle>Infonium</HeaderTitle>
        <HeaderSubtitle>Discover amazing stories and insights</HeaderSubtitle>
        
        {/* 🔇 DISABLED: Auto-refresh notifications removed */}
        {/* <RefreshIndicator 
          visible={isRefreshing || hasNewContent}
          newContent={hasNewContent}
        >
          <RefreshIcon spinning={isRefreshing} />
          {isRefreshing ? 'Checking for new content...' : 
           hasNewContent ? `${newPostIds.size} new articles!` : 'Updating...'}
        </RefreshIndicator> */}
      </Header>
      
      <FeedGrid>
        {posts.map(post => {
          const title = post.title.rendered ? cleanHtml(post.title.rendered) : '';
          const excerpt = post.excerpt.rendered ? cleanHtml(post.excerpt.rendered) : '';
          const mainImageUrl = post.imageUrls && post.imageUrls.length > 0 
            ? post.imageUrls[0]
            : 'https://cdn.infonium.org/img/logo_infonium.png';
          
          const isNewPost = newPostIds.has(post.id);

          return (
            <ArticleCard 
              key={post.id} 
              isNew={isNewPost}
              onClick={() => handleArticleClick(post)}
            >
              {/* 🔇 DISABLED: New post badges removed */}
              {/* {isNewPost && <NewPostBadge>NEW</NewPostBadge>} */}
              <LazyImage 
                src={mainImageUrl} 
                alt={title} 
                placeholder="📰" 
                isMobile={isMobile}
                context="news-feed-grid"
              />
              
              <ArticleContent>
                <ArticleTitle>{title}</ArticleTitle>
                <ArticleExcerpt>
                  {excerpt.length > 150 ? `${excerpt.substring(0, 150)}...` : excerpt}
                </ArticleExcerpt>
                
                <ArticleFooter>
                  <PublishDate>{formatDate(post.date)}</PublishDate>
                  <ReadMoreButton onClick={(e) => {
                    e.stopPropagation();
                    handleArticleClick(post);
                  }}>
                    Read More
                  </ReadMoreButton>
                </ArticleFooter>
              </ArticleContent>
            </ArticleCard>
          );
        })}
      </FeedGrid>
      
      {/* 💻 PC LOADING INDICATOR: Show elegant loading for better UX */}
      {loadingMore && !isMobile && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '30px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '15px',
          margin: '20px auto',
          maxWidth: '400px',
          color: '#fff',
          boxShadow: '0 10px 25px rgba(102, 126, 234, 0.3)',
          animation: 'fadeInScale 0.3s ease-out'
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            border: '2px solid rgba(255,255,255,0.3)',
            borderTop: '2px solid #fff',
            borderRadius: '50%',
            marginRight: '12px',
            animation: 'spin 1s linear infinite'
          }}></div>
          <span style={{ 
            fontSize: '16px',
            fontWeight: '500',
            letterSpacing: '0.5px'
          }}>
            🚀 Loading more great articles...
          </span>
        </div>
      )}
      
      {/* Silent loading for mobile */}
      {loadingMore && isMobile && (
        <div style={{ height: '1px' }} />
      )}
      
      {/* End of content indicator */}
      {!hasMore && posts.length > 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
          <p>🎉 You've viewed all articles!</p>
        </div>
      )}
      
      {/* CSS animations for loading indicator */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes fadeInScale {
          0% {
            opacity: 0;
            transform: scale(0.9) translateY(10px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </Container>
  );
};

export default NewsFeed;
